import { useState, useRef, useCallback } from "react";
import { authHeaders } from "../lib/auth";
import { getMicStream } from "../lib/mic";

/**
 * Voice recorder hook. Adapted from cycle 2.0/web/src/hooks/useVoiceRecorder.ts
 * with the api.transcribeVoice call inlined since our api module
 * does not expose a wrapper of the same name.
 */

type RecordingState = "idle" | "recording" | "processing";

interface TranscribeResult {
  success: boolean;
  text: string;
  error?: string;
}

async function transcribeVoice(audioBase64: string, format: string): Promise<TranscribeResult> {
  try {
    const res = await fetch("/api/voice/transcribe", {
      method: "POST",
      // Carry the Bearer token — /api/* requires it under AUTH_ENABLED (#49).
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ audio_data: audioBase64, audio_format: format })
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        text?: string;
      };
      return { success: false, text: body.text ?? "", error: body.error ?? `HTTP ${res.status}` };
    }
    return (await res.json()) as TranscribeResult;
  } catch (err) {
    return {
      success: false,
      text: "",
      error: err instanceof Error ? err.message : String(err)
    };
  }
}

const getSupportedMimeType = (): string => {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/wav"];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return "audio/webm";
};

const getAudioFormat = (mimeType: string): "webm" | "mp4" | "wav" => {
  if (mimeType.includes("mp4")) return "mp4";
  if (mimeType.includes("wav")) return "wav";
  return "webm";
};

export interface VoiceRecorder {
  state: RecordingState;
  error: string | null;
  toggleRecording: () => Promise<string | null>;
}

export function useVoiceRecorder(): VoiceRecorder {
  const [state, setState] = useState<RecordingState>("idle");
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      if (!navigator.mediaDevices.getUserMedia) {
        setError(
          window.isSecureContext
            ? "当前浏览器不支持麦克风"
            : "麦克风需要 HTTPS（或 localhost）连接"
        );
        setState("idle");
        return;
      }
      const stream = await getMicStream(); // dead-continuity-default fallback (web/src/lib/mic.ts)
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start();
      setState("recording");
    } catch (err) {
      setError(err instanceof Error ? err.message : "麦克风权限被拒绝");
      setState("idle");
    }
  }, []);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (recorder === null || recorder.state === "inactive") {
        setState("idle");
        resolve(null);
        return;
      }

      recorder.onstop = async (): Promise<void> => {
        streamRef.current?.getTracks().forEach((t) => t.stop());

        const mimeType = recorder.mimeType;
        const blob = new Blob(chunksRef.current, { type: mimeType });

        setState("processing");

        try {
          const buffer = await blob.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          let binary = "";
          bytes.forEach((b) => (binary += String.fromCharCode(b)));
          const base64 = btoa(binary);

          const format = getAudioFormat(mimeType);
          const result = await transcribeVoice(base64, format);

          setState("idle");

          if (result.success && result.text.length > 0) {
            resolve(result.text);
          } else {
            setError(result.error ?? "转写失败");
            resolve(null);
          }
        } catch (err) {
          setState("idle");
          setError(err instanceof Error ? err.message : "转写失败");
          resolve(null);
        }
      };

      recorder.stop();
    });
  }, []);

  const toggleRecording = useCallback(async (): Promise<string | null> => {
    if (state === "recording") return stopRecording();
    if (state === "idle") await startRecording();
    return null;
  }, [state, startRecording, stopRecording]);

  return { state, error, toggleRecording };
}
